// BoreHub web API client — talks to the SAME FastAPI backend as the mobile app.
const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const TOKEN_KEY = "borehub_token";
const USER_KEY = "borehub_user";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { localStorage.setItem(TOKEN_KEY, t); clearApiCache(); };
export const clearToken = () => { localStorage.removeItem(TOKEN_KEY); clearApiCache(); };
export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};
export const setStoredUser = (u) => localStorage.setItem(USER_KEY, JSON.stringify(u));
export const clearStoredUser = () => localStorage.removeItem(USER_KEY);

async function req(path, init = {}) {
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const r = await fetch(`${API}${path}`, { ...init, headers });
  if (r.status === 401 && !path.startsWith("/auth/")) {
    // Session expired/invalid — clear it and let the app show a clean re-login.
    clearToken();
    clearStoredUser();
    try { window.dispatchEvent(new CustomEvent("auth:unauthorized")); } catch { /* noop */ }
  }
  if (!r.ok) {
    let msg = `Request failed (${r.status})`;
    try {
      const j = await r.json();
      msg = j.detail || msg;
    } catch {
      /* noop */
    }
    throw new Error(msg);
  }
  if (r.status === 204) return null;
  const ct = r.headers.get("content-type") || "";
  return ct.includes("application/json") ? r.json() : r.text();
}

// Lightweight GET cache with stale-while-revalidate: serves cached data instantly
// (so navigating between tabs feels immediate) and refreshes it in the background.
// Any write clears it so data never goes stale after a mutation.
const FRESH_TTL = 10000;   // within this, use cache with NO background refresh
const STALE_TTL = 180000;  // within this, serve cache instantly + refresh in background; beyond, block & refetch
const cache = new Map(); // path -> { data, ts }
const inflight = new Map(); // path -> Promise
export const clearApiCache = () => { cache.clear(); inflight.clear(); };

function revalidate(path) {
  const p = req(path)
    .then((data) => { cache.set(path, { data, ts: Date.now() }); inflight.delete(path); return data; })
    .catch((e) => { inflight.delete(path); throw e; });
  inflight.set(path, p);
  return p;
}

function cachedGet(path, { force = false } = {}) {
  const hit = cache.get(path);
  const now = Date.now();
  if (!force && hit && now - hit.ts < STALE_TTL) {
    if (now - hit.ts > FRESH_TTL && !inflight.has(path)) {
      revalidate(path).catch(() => {}); // stale-while-revalidate: refresh in background
    }
    return Promise.resolve(hit.data);
  }
  if (!force) {
    const pending = inflight.get(path);
    if (pending) return pending;
  }
  return revalidate(path);
}

export const api = {
  get: (p, opts) => cachedGet(p, opts),
  // Return cached data synchronously (or undefined) — used to seed instant renders.
  peek: (p) => cache.get(p)?.data,
  // Warm the cache ahead of a click (e.g. on hover); errors are swallowed.
  prefetch: (p) => { cachedGet(p).catch(() => {}); },
  post: (p, b) => { clearApiCache(); return req(p, { method: "POST", body: b ? JSON.stringify(b) : undefined }); },
  put: (p, b) => { clearApiCache(); return req(p, { method: "PUT", body: b ? JSON.stringify(b) : undefined }); },
  del: (p) => { clearApiCache(); return req(p, { method: "DELETE" }); },
  uploadBase64: async (dataUri) => {
    const r = await req("/upload-base64", { method: "POST", body: JSON.stringify({ data: dataUri }) });
    return r.url;
  },
  // Binary multipart upload (no base64 bloat) — preferred for photo uploads.
  uploadFile: async (blob, filename = "photo.jpg") => {
    const fd = new FormData();
    fd.append("file", blob, filename);
    const r = await req("/upload", { method: "POST", body: fd });
    return r.url;
  },
};

export const BACKEND_URL = BASE;

// Turn a stored photo value into a displayable URL (adds backend origin + auth token).
// Pass `width` to request a resized JPEG thumbnail from the backend (much faster for lists).
export const resolveFileUri = (stored, width) => {
  if (!stored) return "";
  if (stored.startsWith("data:") || stored.startsWith("http")) return stored;
  const sep = stored.includes("?") ? "&" : "?";
  const w = width ? `&w=${width}` : "";
  return `${BASE}${stored}${sep}token=${getToken() || ""}${w}`;
};

// URL that makes the browser download the full-resolution original natively
// (backend streams it with Content-Disposition), avoiding the slow fetch→blob path.
export const fileDownloadUri = (stored) => {
  if (!stored || stored.startsWith("data:") || stored.startsWith("http")) return stored;
  const sep = stored.includes("?") ? "&" : "?";
  return `${BASE}${stored}${sep}token=${getToken() || ""}&download=1`;
};
